import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const NAVY_BLUE_ARGB = "FF003366";
const NAVY_BLUE_RGB = [0, 51, 102];

const formatTime = (raw) => {
    if (!raw) return "—";
    const timePart = String(raw).split(".")[0];
    const [hStr, mStr] = timePart.split(":");
    if (hStr === undefined || mStr === undefined) return raw;
    const h = parseInt(hStr, 10);
    const m = mStr.padStart(2, "0");
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${String(h12).padStart(2, "0")}:${m} ${period}`;
};

const formatDate = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatStatusLabel = (status) => {
    const key = status?.toLowerCase();
    const statusMap = {
        present: "Present",
        absent: "Absent",
        late: "Late",
    };
    return statusMap[key] ?? status ?? "—";
};

export const MANAGE_ATTENDANCE_EXPORT_COLUMNS = [
    { label: "Emp ID", getValue: (row) => row.employeeId ?? "—" },
    {
        label: "Emp Code",
        getValue: (row) => row.empId ?? row.empCode ?? "—",
    },
    { label: "Employee Name", getValue: (row) => row.employeeName ?? "—" },
    {
        label: "Department",
        getValue: (row) => row.department ?? row.departmentName ?? "—",
    },
    {
        label: "Designation",
        getValue: (row) => row.designation ?? row.designationName ?? "—",
    },
    { label: "Master Shift", getValue: (row) => row.masterShift ?? "—" },
    { label: "Actual Shift", getValue: (row) => row.actualShift ?? "—" },
    { label: "Zone", getValue: (row) => row.zoneName ?? "—" },
    {
        label: "Ward",
        getValue: (row) => row.circleName ?? row.wardNo ?? row.wardName ?? "—",
    },
    { label: "Device ID", getValue: (row) => row.deviceId ?? "—" },
    { label: "Date", getValue: (row) => row.date ?? "—" },
    { label: "Punch In", getValue: (row) => row.punchIn ?? "—" },
    { label: "Punch Out", getValue: (row) => row.punchOut ?? "—" },
    { label: "Total Time", getValue: (row) => row.totalTime ?? "—" },
    {
        label: "Status",
        getValue: (row) => formatStatusLabel(row.status ?? row.Status),
    },
];

export const VIEW_ATTENDANCE_EXPORT_COLUMNS = [
    {
        label: "Emp Code",
        getValue: (row) => row.employeeCode ?? row.empCode ?? row.empId ?? "—",
    },
    { label: "Employee Name", getValue: (row) => row.employeeName ?? "—" },
    {
        label: "Designation",
        getValue: (row) => row.designationName ?? row.designation ?? "—",
    },
    {
        label: "Department",
        getValue: (row) => row.departmentName ?? row.department ?? "—",
    },
    { label: "Master Shift", getValue: (row) => row.masterShift ?? "—" },
    { label: "Actual Shift", getValue: (row) => row.actualShift ?? "—" },
    { label: "Zone", getValue: (row) => row.zoneName ?? "—" },
    {
        label: "Ward",
        getValue: (row) => row.circleName ?? row.wardName ?? row.wardNo ?? "—",
    },
    {
        label: "KPI Category",
        getValue: (row) => row.kpiCategoryName ?? row.kpiCategory ?? "—",
    },
    { label: "Device ID", getValue: (row) => row.deviceId ?? "—" },
    { label: "Date", getValue: (row) => formatDate(row.date) },
    { label: "Punch In", getValue: (row) => formatTime(row.punchIn) },
    { label: "Punch Out", getValue: (row) => formatTime(row.punchOut) },
    {
        label: "Status",
        getValue: (row) => formatStatusLabel(row.status ?? row.Status),
    },
];

const getTimestamp = () => {
    const d = new Date();
    const date = `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
    const time = `${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}`;
    return { date, time, full: `${date}, ${time}` };
};

const buildHeaders = (columns) => ["S.No", ...columns.map((column) => column.label)];

const mapRow = (item, index, columns) => [
    index + 1,
    ...columns.map((column) => column.getValue(item, index)),
];

const applyExcelHeaderStyle = (cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: NAVY_BLUE_ARGB },
    };
};

export const ExportAttendanceExcel = async ({
    attendanceData = [],
    columns = VIEW_ATTENDANCE_EXPORT_COLUMNS,
    reportTitle = "Attendance Report",
    fileName = "AttendanceReport",
}) => {
    const { full } = getTimestamp();
    const headers = buildHeaders(columns);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Attendance");
    const boldFont = { bold: true };

    worksheet.addRow([]);
    worksheet.mergeCells(1, 1, 1, headers.length);
    const tsCell = worksheet.getCell(1, 1);
    tsCell.value = `${reportTitle} Generated on: ${full}`;
    tsCell.font = boldFont;
    tsCell.alignment = { horizontal: "center" };

    worksheet.addRow([]);
    worksheet.mergeCells(2, 1, 2, headers.length);
    const titleCell = worksheet.getCell(2, 1);
    titleCell.value = reportTitle;
    titleCell.font = boldFont;
    titleCell.alignment = { horizontal: "center" };

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell({ includeEmpty: true }, (cell) => {
        applyExcelHeaderStyle(cell);
    });

    attendanceData.forEach((item, index) => {
        worksheet
            .addRow(mapRow(item, index, columns))
            .eachCell({ includeEmpty: true }, (cell) => {
                cell.alignment = { horizontal: "center", vertical: "middle" };
            });
    });

    worksheet.columns = headers.map((header) => ({
        width: Math.max(12, String(header).length + 2),
    }));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}_${new Date().toISOString().split("T")[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

export const ExportAttendancePDF = async ({
    attendanceData = [],
    columns = VIEW_ATTENDANCE_EXPORT_COLUMNS,
    reportTitle = "Attendance Report",
    fileName = "AttendanceReport",
}) => {
    const { full } = getTimestamp();
    const headers = buildHeaders(columns);
    const doc = new jsPDF("landscape", "mm", "a4");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(reportTitle, 14, 18);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Generated on: ${full}`, 14, 26);

    autoTable(doc, {
        startY: 32,
        head: [headers],
        body: attendanceData.map((item, index) => mapRow(item, index, columns)),
        headStyles: {
            fillColor: NAVY_BLUE_RGB,
            textColor: 255,
            fontStyle: "bold",
            fontSize: 7,
            halign: "center",
        },
        styles: {
            fontSize: 7,
            halign: "center",
            cellPadding: 2,
        },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { top: 10, left: 5, right: 5 },
    });

    doc.save(`${fileName}_${new Date().toISOString().split("T")[0]}.pdf`);
};
